import { Component, inject, ChangeDetectorRef } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Subscription, switchMap, map } from "rxjs";
import { environment } from "../../../environments/environment";
import { SocketService } from "../../core/event/services/socket.service";
import { AlertService } from "../alert/services/alert.service";
import { EventMetadata, EventSource } from "../../core/models/event-metadata.model";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import { CommonModule, CurrencyPipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { HttpClient } from "@angular/common/http";
import { GoalHttpService } from "./services/goal-http.service";

// Constantes
const DEFAULT_GOAL_FINAL_VALUE = 100000; // Em centavos
const EXPLOSION_INITIAL_DELAY = 800;
const EXPLOSION_SCATTER_DELAY = 2000;
const EXPLOSION_FADE_DELAY = 600;
const ANIMATION_DURATION = "800ms";
const EXPLOSION_SCALE_EXPLODE = 1.2;
const EXPLOSION_SCALE_SCATTER = 0.8;

// Textos da interface
const DEFAULT_GOAL_TEXT = 'Meta';
const DEFAULT_NEXT_STEP_TEXT = 'Próxima meta';
const DEFAULT_PROGRESS_TEXT = 'R$ 0 / R$ 0';
const RESET_GOAL_CONFIRMATION = 'Are you sure you want to reset the goal?';

enum EventType {
  DONATION = "donation",
  SALE = "sale",
  ALERT_SETTINGS_UPDATED = "alert_settings_updated",
}

@Component({
  standalone: true,
  selector: "app-goal",
  templateUrl: "./goal.component.html",
  styleUrls: ["./goal.component.scss"],
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule],
  providers: [SocketService, CurrencyPipe, HttpClient, GoalHttpService],
  animations: [
    trigger("progressAnimation", [
      transition("* => *", [
        animate(ANIMATION_DURATION + " ease-out")
      ])
    ]),
    trigger("explosionAnimation", [
      state("idle", style({ 
        transform: "scale(0)", 
        opacity: 0 
      })),
      state("explode", style({ 
        transform: `scale(${EXPLOSION_SCALE_EXPLODE})`, 
        opacity: 1 
      })),
      state("scatter", style({ 
        transform: `scale(${EXPLOSION_SCALE_SCATTER}) translateX(var(--scatter-x)) translateY(var(--scatter-y)) rotate(var(--rotation))`, 
        opacity: 0 
      })),
      transition("idle => explode", animate(`${EXPLOSION_INITIAL_DELAY}ms cubic-bezier(0.68, -0.55, 0.265, 1.55)`)),
      transition("explode => scatter", animate(`${EXPLOSION_SCATTER_DELAY}ms ease-out`)),
      transition("scatter => idle", animate(`${EXPLOSION_FADE_DELAY}ms ease-in`))
    ]),
    trigger("textFadeIn", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(20px)" }),
        animate(`${ANIMATION_DURATION} 1000ms ease-out`, style({ opacity: 1, transform: "translateY(0)" }))
      ])
    ])
  ],
})
export class GoalComponent {
  constructor() {}

  // Parâmetros da URL
  public hashGoal!: string;
  public streamerId: string = "";
  public paramSubscriptions: Subscription[] = [];

  public goalSteps: any[] = [];
  public currentStepIndex: number = 0;
  public incluirDoacoes: boolean = true;
  public incluirVendas: boolean = true;
  // Configurações visuais da tabela goalvisualsetting (nomes em inglês)
  public barColor: string = "#9c27b0";
  public textColor: string = "#333333";
  public valueColor: string = "#ffffff"; // Cor para texto dentro da barra
  public backgroundColor: string = "rgba(255, 255, 255, 0.95)";
  public goalCompletedText: string = "Goal achieved!";
  public showExplosion: boolean = true;
  public showGoalText: boolean = false;
  
  // 🎯 Notificação de evento (dual-icon system)
  public showNotification: boolean = false;
  public notificationData: {
    platformIconPath?: string;
    platformColor?: string;
    typeIcon?: string; // Material Icon name
    typeIconPath?: string; // PNG image path
    contributorName: string;
    displayValue: string;
  } | null = null;

  // Configuração de PLATAFORMAS
  private readonly PLATFORM_CONFIG = {
    TWITCH: {
      iconPath: 'assets/images/platforms/twitch.svg',
      color: '#9146FF',
    },
    KICK: {
      iconPath: 'assets/images/platforms/kick.svg',
      color: '#53FC18',
    },
    YOUTUBE: {
      iconPath: 'assets/images/platforms/youtube.png',
      color: '#FF0000',
    },
    SITE: {
      iconPath: 'assets/images/logo_index.png',
      color: '#9c27b0',
    },
  };

  // Configuração de TIPOS DE EVENTO
  private readonly EVENT_TYPE_CONFIG = {
    SUB: {
      icon: 'star',
      label: 'Sub',
    },
    GIFT_SUB: {
      icon: 'card_giftcard',
      label: 'Gift',
    },
    BITS: {
      iconPath: 'assets/images/platforms/bits.png',
      label: 'Bits',
    },
    KICKS: {
      iconPath: 'assets/images/platforms/kicks.png',
      label: 'Kicks',
    },
    SUPERCHAT: {
      icon: 'chat_bubble',
      label: 'Super Chat',
    },
    MEMBER: {
      icon: 'workspace_premium',
      label: 'Membro',
    },
    DONATION: {
      icon: 'favorite',
      label: 'Doação',
    },
    SALE: {
      icon: 'shopping_cart',
      label: 'Venda',
    },
  };

  // MAPEAMENTO de EventSource → Platform + Type
  private readonly EVENT_SOURCE_MAP = {
    [EventSource.TWITCH_SUB]: {
      platform: 'TWITCH' as const,
      type: 'SUB' as const,
    },
    [EventSource.KICK_SUB]: {
      platform: 'KICK' as const,
      type: 'SUB' as const,
    },
    [EventSource.YOUTUBE_MEMBER]: {
      platform: 'YOUTUBE' as const,
      type: 'MEMBER' as const,
    },
    [EventSource.TWITCH_BITS]: {
      platform: 'TWITCH' as const,
      type: 'BITS' as const,
    },
    [EventSource.KICK_KICKS]: {
      platform: 'KICK' as const,
      type: 'KICKS' as const,
    },
    [EventSource.YOUTUBE_SUPERCHAT]: {
      platform: 'YOUTUBE' as const,
      type: 'SUPERCHAT' as const,
    },
    [EventSource.DONATION]: {
      platform: null,
      type: 'DONATION' as const,
    },
    [EventSource.SALE]: {
      platform: null,
      type: 'SALE' as const,
    },
  };
  
  public configuredValue: number = 0; // Valor configurável para simulação
  public totalRaised: number = 0; // Valor real do backend
  public metaAtingida: boolean = false;
  
  // Explosão de ícones
  public explosionIcons: any[] = [];
  public explosionState: string = 'idle';

  // Flags
  public isLocal = false;

  // Services
  public titleService = inject(Title);
  public activatedRoute = inject(ActivatedRoute);
  public socketService = inject(SocketService);
  public alertService = inject(AlertService);
  public http = inject(HttpClient);
  public goalHttpService = inject(GoalHttpService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.isLocal = environment.stage === "local";
    this.initializeExplosionIcons();
    this.initializeParams();
    this.connectToSocket();
  }

  ngOnDestroy(): void {
    this.socketService.disconnect();
    this.paramSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initializeParams(): void {
    this.isLocal = environment.stage === "local";
    
    const paramSub = this.activatedRoute.params
      .pipe(
        switchMap((params: any) => {
          this.hashGoal = params?.id;
          this.titleService.setTitle("Meta - The Cool Rhyno");
          return this.activatedRoute.queryParams;
        }),
        map((queryParams: any) => {
          return queryParams;
        })
      )
      .subscribe();

    this.paramSubscriptions.push(paramSub);
  }

  private connectToSocket(): void {
    if (this.hashGoal) {
      this.socketService.connect();
      
      // Carregamento inicial via HTTP
      this.goalHttpService.getGoalByHash(this.hashGoal).subscribe({
        next: (response: any) => {
          if (response) {
            this.streamerId = response.id; // Necessário para eventos WebSocket
            this.loadGoalData(response.goal);
            this.loadVisualSettings(response.goalVisualSettings || null);
          } else {
            console.error("❌ [GOAL] Nenhum dado retornado para hash:", this.hashGoal);
          }
        },
        error: (error) => {
          console.error("❌ [GOAL] Erro na requisição HTTP:", error);
        }
      });
      
      // WebSocket para atualizações em tempo real
      this.socketService
        .onEvent(`event:${this.hashGoal}`)
        .subscribe({
          next: (eventPayload: any) => {
            // Tratar evento de meta deletada
            if (eventPayload.eventType === 'GOAL_DELETED') {
              this.handleGoalDeleted(eventPayload);
              return;
            }

            // Tratar evento de meta criada/atualizada
            if (eventPayload.eventType === 'GOAL_DATA_UPDATED') {
              this.handleGoalCreatedOrUpdated(eventPayload);
              return;
            }
            
            if (eventPayload.eventType === EventType.DONATION && this.incluirDoacoes) {
              this.handleBackendEvent(eventPayload.amount);
            }
            
            if (eventPayload.eventType === EventType.SALE && this.incluirVendas) {
              this.handleBackendEvent(eventPayload.amount);
            }
          },
          error: (error) => {
            console.error("❌ [GOAL] Erro no WebSocket:", error);
          }
        });
      // WebSocket para eventos de configuração
      this.socketService
        .onEvent(`event:config:${this.hashGoal}`)
        .subscribe({
          next: (eventPayload: any) => {
            if (eventPayload.eventType === EventType.ALERT_SETTINGS_UPDATED) {
              this.loadGoalSettings(eventPayload.alertSettings || []);
            }
          },
          error: (error) => {
            console.error("❌ [GOAL] Erro no WebSocket de configuração:", error);
          }
        });

      // Eventos específicos para configurações visuais
      this.socketService
        .onEvent(`goal-visual-updated:${this.hashGoal}`)
        .subscribe((visualSettings: any) => {
          this.updateVisualSettings(visualSettings);
        });

      // Eventos específicos para dados da meta
      this.socketService
        .onEvent(`goal-updated:${this.hashGoal}`)
        .subscribe((goalData: any) => {
          this.updateGoalData(goalData);
        });
    } else {
      console.error("❌ [GOAL] HashGoal não definido");
    }
  }



  private loadGoalSettings(alertSettings: any[]): void {
    // Usar o AlertService atualizado
    this.alertService.loadConfigFromSocket(alertSettings);
    
    // Subscrever às configurações
    const goalSubscription = this.alertService.settingsGoal.subscribe(goalSettings => {
      this.goalSteps = goalSettings.goalSteps || [];
      this.currentStepIndex = goalSettings.currentStepIndex || 0;
      this.totalRaised = (goalSettings.totalRaised || 0) / 100; // ✅ CONVERSÃO: Centavos → Real
      this.configuredValue = (goalSettings.configuredValue || 0) / 100; // ✅ CONVERSÃO: Centavos → Real
      
      // Configurações visuais e comportamento
      this.incluirDoacoes = goalSettings.goalIncluirDoacoes;
      this.incluirVendas = goalSettings.goalIncluirVendas;
      this.barColor = goalSettings.barColor;
      this.textColor = goalSettings.textColor;
      this.valueColor = goalSettings.valueColor;
      this.backgroundColor = goalSettings.backgroundColor;
      this.goalCompletedText = goalSettings.goalCompletedText;
      this.showExplosion = goalSettings.showExplosion;
      this.showGoalText = goalSettings.showGoalText;
    });
    
    this.paramSubscriptions.push(goalSubscription);
  }

  private adicionarValor(valor: number): void {
    const valorAnterior = this.configuredValue;
    this.configuredValue += valor;
    
    this.checkStepCompletion();
    
    const currentStep = this.getCurrentStep();
    const finalEtapa = currentStep?.goalValueFinal || 0;
  }

  private initializeExplosionIcons(): void {
    this.explosionIcons = [
      { type: 'image', content: 'assets/images/rhino.png', alt: 'Rinoceronte' },
      { type: 'emoji', content: '💥' },
      { type: 'emoji', content: '⭐' },
      { type: 'emoji', content: '🎉' },
      { type: 'emoji', content: '🎊' },
      { type: 'emoji', content: '🚀' },
      { type: 'emoji', content: '🎯' },
      { type: 'emoji', content: '💎' },
      { type: 'emoji', content: '🏆' },
      { type: 'emoji', content: '✨' },
      { type: 'emoji', content: '🌟' },
      { type: 'emoji', content: '🔥' }
    ];
  }

  private celebrarMeta(): void {
    if (this.showExplosion) {
      this.startIconExplosion();
    }
  }

  private startIconExplosion(): void {
    // Iniciar com explosão
    this.explosionState = 'explode';
    
    // Após tempo definido, dispersar os ícones
    setTimeout(() => {
      this.explosionState = 'scatter';
      
      // Após tempo de dispersão, fazer fade out
      setTimeout(() => {
        this.explosionState = 'idle';
        
        // Reset para permitir nova explosão
        setTimeout(() => {
          this.explosionState = 'idle';
        }, EXPLOSION_FADE_DELAY);
      }, EXPLOSION_SCATTER_DELAY);
    }, EXPLOSION_INITIAL_DELAY);
  }

  private getCurrentStep(): any | null {
    return this.goalSteps[this.currentStepIndex] || null;
  }

  private checkStepCompletion(): void {
    const currentStep = this.getCurrentStep();
    if (!currentStep) return;
    
    if (this.configuredValue >= currentStep.goalValueFinal && !currentStep.isCompleted) {
      // Marcar etapa como completa
      currentStep.isCompleted = true;
      currentStep.completedAt = new Date();
      currentStep.actualValueWhenCompleted = this.configuredValue;
      
      // Avançar para próxima etapa se existir
      if (this.currentStepIndex < this.goalSteps.length - 1) {
        this.currentStepIndex++;
        const nextStep = this.getCurrentStep();
        if (nextStep) {
          nextStep.goalValueInicial = this.configuredValue; // Continuar do valor atual
        }
      } else {
        // Todas as etapas concluídas
        this.metaAtingida = true;
        this.celebrarMeta();
      }
    }
  }

  public updateRealValueFromBackend(newTotalRaised: number): void {
    this.totalRaised = Math.max(0, (newTotalRaised || 0) / 100); // ✅ CONVERSÃO: Centavos → Real
  }

  public handleBackendEvent(eventAmount: number): void {
    const safeAmount = typeof eventAmount === 'number' && !isNaN(eventAmount) && eventAmount > 0 ? eventAmount : 0;
    
    if (safeAmount > 0) {
      const safeAmountInReais = safeAmount / 100; // ✅ CONVERSÃO: Centavos → Real
      this.totalRaised += safeAmountInReais;
      this.configuredValue += safeAmountInReais;
      this.checkStepCompletion();
    }
  }

  /**
   * Tratar evento de meta deletada - esconder widget
   */
  public handleGoalDeleted(eventPayload: any): void {
    // Esconder o widget completamente
    this.hideWidget();
    
    // Resetar dados
    this.totalRaised = 0;
    this.configuredValue = 0;
    this.currentStepIndex = 0;
    
    // Forçar detecção de mudanças
    this.cdr.detectChanges();
  }

  /**
   * Esconder widget do OBS
   */
  private hideWidget(): void {
    const widgetElement = document.querySelector('.goal-container, .goal-widget, .app-goal');
    if (widgetElement) {
      (widgetElement as HTMLElement).style.display = 'none';
    }
  }

  /**
   * Mostrar widget do OBS
   */
  private showWidget(): void {
    const widgetElement = document.querySelector('.goal-container, .goal-widget, .app-goal');
    if (widgetElement) {
      (widgetElement as HTMLElement).style.display = 'flex';
    }
  }

  /**
   * Tratar evento de meta criada/atualizada - mostrar widget e recarregar
   */
  public handleGoalCreatedOrUpdated(eventPayload: any): void {
    // Mostrar o widget se estava escondido
    this.showWidget();
    
    // Se há dados da meta no evento, aplicar diretamente
    if (eventPayload.goalSteps || eventPayload.totalRaised !== undefined) {
      this.loadGoalDataFromWebSocket(eventPayload);
    } else {
      // Caso contrário, recarregar via HTTP para garantir dados completos
      this.reloadGoalData();
    }
    
    // Forçar detecção de mudanças
    this.cdr.detectChanges();
  }

  /**
   * ✅ CARREGAR DADOS DA META VIA WEBSOCKET
   */
  private loadGoalDataFromWebSocket(goalData: any): void {
    // 🎯 NOVO: Exibir notificação se houver evento
    if (goalData.event) {
      this.showEventNotification(goalData.event);
    }

    if (goalData.totalRaised !== undefined) {
      this.totalRaised = (goalData.totalRaised || 0) / 100;
    }
    if (goalData.configuredValue !== undefined) {
      this.configuredValue = (goalData.configuredValue || 0) / 100;
    }
    if (goalData.currentStepIndex !== undefined) {
      this.currentStepIndex = goalData.currentStepIndex || 0;
    }
    if (goalData.goalSteps) {
      this.goalSteps = goalData.goalSteps.map((step: any) => ({
        goalDescricao: step.title || step.description,
        goalValueInicial: (step.initialAmount || 0) / 100,
        goalValueFinal: (step.targetAmount || 0) / 100,
        goalMostrarPreview: step.showNextPreview || false,
        isCompleted: step.completed || false,
        completedAt: step.completedAt ? new Date(step.completedAt) : undefined,
        actualValueWhenCompleted: step.actualValueWhenCompleted ? (step.actualValueWhenCompleted / 100) : undefined
      }));
    }
  }

  /**
   * Recarregar dados da meta via HTTP
   */
  private reloadGoalData(): void {
    if (this.hashGoal) {
      this.goalHttpService.getGoalByHash(this.hashGoal).subscribe({
        next: (response: any) => {
          if (response && response.goals) {
            this.loadGoalData(response.goals);
          }
        },
        error: (error) => {
          console.error("❌ [GOAL] Erro ao recarregar dados:", error);
        }
      });
    }
  }

  // Getters atualizados para template
  get progressoPercentual(): number {
    const currentStep = this.getCurrentStep();
    if (!currentStep) return 0;
    
    const currentValue = this.configuredValue || 0;
    const valorInicial = 0; // Sempre zero como referência
    const valorFinal = currentStep.goalValueFinal || 0;
    
    if (valorFinal <= 0) return 0;
    
    // Calcular progresso baseado no valor atual vs meta final (inicial sempre zero)
    const valueInCurrentStep = Math.max(currentValue - valorInicial, 0);
    const stepRange = valorFinal - valorInicial;
    
    const progresso = stepRange > 0 ? (valueInCurrentStep / stepRange) * 100 : 0;
    return Math.min(Math.max(progresso, 0), 100);
  }

  get valorRestante(): number {
    const currentStep = this.getCurrentStep();
    if (!currentStep) return 0;
    return Math.max(currentStep.goalValueFinal - this.configuredValue, 0);
  }

  get progressoTexto(): string {
    const currentStep = this.getCurrentStep();
    if (!currentStep) return DEFAULT_PROGRESS_TEXT;
    
    const currencyPipe = new CurrencyPipe("pt-BR");
    const atual = currencyPipe.transform(this.configuredValue, "BRL", "symbol", "1.2-2");
    const final = currencyPipe.transform(currentStep.goalValueFinal, "BRL", "symbol", "1.2-2");
    return `${atual} / ${final}`;
  }

  get descricao(): string {
    const currentStep = this.getCurrentStep();
    return currentStep?.goalDescricao || DEFAULT_GOAL_TEXT;
  }

  get shouldShowNextStep(): boolean {
    const currentStep = this.getCurrentStep();
    const hasNextStep = this.currentStepIndex < this.goalSteps.length - 1;
    return currentStep?.goalMostrarPreview && hasNextStep && !this.metaAtingida;
  }

  get nextStepDescription(): string {
    if (this.currentStepIndex < this.goalSteps.length - 1) {
      const nextStep = this.goalSteps[this.currentStepIndex + 1];
      return nextStep?.goalDescricao || DEFAULT_NEXT_STEP_TEXT;
    }
    return '';
  }

  public onDeleteGoal(): void {
    if (this.isLocal) {
      if (confirm(RESET_GOAL_CONFIRMATION)) {
        this.configuredValue = 0;
        this.totalRaised = 0;
        this.currentStepIndex = 0;
        this.metaAtingida = false;
        
        this.goalSteps.forEach(step => {
          step.isCompleted = false;
          step.completedAt = undefined;
          step.actualValueWhenCompleted = undefined;
        });
      }
    }
  }

  public onTestExplosion(): void {
    if (this.isLocal) {
      this.metaAtingida = true;
      this.showExplosion = true;
      this.startIconExplosion();
    }
  }

  public onTestNotification(): void {
    if (this.isLocal) {
      // Mock de evento para teste
      const mockEvent: EventMetadata = {
        source: EventSource.TWITCH_SUB,
        contributorName: 'TestUser123',
        amountInCents: 2500, // R$ 25,00
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestDonation(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.DONATION,
        contributorName: 'Doador Generoso',
        amountInCents: 5000, // R$ 50,00
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestSale(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.SALE,
        contributorName: 'Cliente VIP',
        amountInCents: 15000, // R$ 150,00
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestYoutubeMember(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.YOUTUBE_MEMBER,
        contributorName: 'YouTuber Fan',
        amountInCents: 1000, // R$ 10,00
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }

  public onTestYoutubeSuperChat(): void {
    if (this.isLocal) {
      const mockEvent: EventMetadata = {
        source: EventSource.YOUTUBE_SUPERCHAT,
        contributorName: 'Super Chat Donor',
        amountInCents: 5000, // R$ 50,00
        isGift: false
      };
      this.showEventNotification(mockEvent);
    }
  }



  /**
   * Configura os listeners para eventos de progresso da meta
   */
  private setupGoalEventListeners(): void {
    // Escutar eventos de progresso da meta
    this.socketService
      .onEvent(`event:${this.hashGoal}`)
      .subscribe((eventPayload: any) => {
        if (eventPayload.type === "goal") {
          this.handleGoalProgressEvent(eventPayload.data);
        }
      });
  }

  /**
   * Carrega configurações visuais (mesmo padrão dos dados da meta)
   */
  private loadVisualSettings(visualSettings: any): void {
    if (visualSettings) {
      // Aplicar configurações visuais
      this.barColor = visualSettings.barColor || this.barColor;
      this.textColor = visualSettings.textColor || this.textColor;
      this.valueColor = visualSettings.valueColor || this.valueColor;
      this.backgroundColor = visualSettings.backgroundColor || this.backgroundColor;
      this.showExplosion = visualSettings.showExplosion ?? this.showExplosion;
      this.showGoalText = visualSettings.showGoalText ?? this.showGoalText;
      this.goalCompletedText = visualSettings.goalCompletedText || this.goalCompletedText;
      
      // Forçar detecção de mudanças
      this.cdr.detectChanges();
    }
  }

  /**
   * Carrega dados reais da meta ativa do backend
   */
  private loadGoalData(goal: any): void {
    // Encontrar a meta ativa
    const activeGoal = goal || null;
    
    if (activeGoal) {
      // Conversão: Centavos → Reais para dados HTTP
      this.totalRaised = (activeGoal.totalRaised || 0) / 100;
      
      // Usar configuredValue para simulação visual
      this.configuredValue = (activeGoal.configuredValue || 0) / 100;
      
      // Carregar etapas da meta
      if (activeGoal.steps && activeGoal.steps.length > 0) {
        this.goalSteps = activeGoal.steps.map((step: any) => ({
          goalDescricao: step.description,
          goalValueInicial: (step.initialValue || 0) / 100,
          goalValueFinal: (step.finalValue || 0) / 100,
          goalMostrarPreview: step.showNextPreview,
          isCompleted: step.completed || false,
          completedAt: step.completedAt ? new Date(step.completedAt) : undefined,
          actualValueWhenCompleted: step.actualValueWhenCompleted ? (step.actualValueWhenCompleted / 100) : undefined
        }));
        
        // Determinar etapa atual
        this.currentStepIndex = activeGoal.currentStepIndex || 0;
      }
      
      // Verificar se meta está atingida
      this.checkStepCompletion();
      
      // Forçar detecção de mudanças após carregar dados
      this.cdr.detectChanges();
    } else {
      // Usar valores padrão se não houver meta ativa
      this.goalSteps = [];
      this.currentStepIndex = 0;
      this.totalRaised = 0;
      this.configuredValue = 0;
    }
    
    // Configurar listeners de eventos após carregar dados
    this.setupGoalEventListeners();
  }

  /**
   * Manipula eventos de progresso da meta
   */
  private handleGoalProgressEvent(goalData: any): void {
    // Conversão: Centavos → Reais para eventos de progresso
    this.configuredValue = (goalData.configuredValue || 0) / 100;
    this.totalRaised = (goalData.totalRaised || 0) / 100;
    this.currentStepIndex = goalData.currentStepIndex || 0;
    
    // Verificar se a meta foi atingida
    const wasCompleted = this.metaAtingida;
    this.metaAtingida = goalData.isCompleted || false;
    
    // Se acabou de ser atingida, mostrar explosão
    if (!wasCompleted && this.metaAtingida && this.showExplosion) {
      this.startIconExplosion();
    }
  }

  /**
   * Atualiza configurações visuais em tempo real
   */
  private updateVisualSettings(visualSettings: any): void {
    // Atualizar cores e configurações visuais
    this.barColor = visualSettings.barColor || this.barColor;
    this.textColor = visualSettings.textColor || this.textColor;
    this.valueColor = visualSettings.valueColor || this.valueColor;
    this.backgroundColor = visualSettings.backgroundColor || this.backgroundColor;
    this.showExplosion = visualSettings.showExplosion ?? this.showExplosion;
    this.showGoalText = visualSettings.showGoalText ?? this.showGoalText;
    this.goalCompletedText = visualSettings.goalCompletedText || this.goalCompletedText;
    
    // Forçar detecção de mudanças para atualizar o template
    this.cdr.detectChanges();
  }

  /**
   * Atualiza dados da meta em tempo real
   */
  private updateGoalData(goalData: any): void {
    // Atualizar dados da meta
    this.totalRaised = (goalData.totalRaised || this.totalRaised || 0) / 100;
    this.configuredValue = (goalData.configuredValue || this.configuredValue || 0) / 100;
    this.currentStepIndex = goalData.currentStepIndex ?? this.currentStepIndex;
    this.incluirDoacoes = goalData.incluirDoacoes ?? this.incluirDoacoes;
    this.incluirVendas = goalData.incluirVendas ?? this.incluirVendas;
    
    // Atualizar etapas se fornecidas
    if (goalData.steps && Array.isArray(goalData.steps)) {
      this.goalSteps = goalData.steps.map((step: any) => ({
        goalDescricao: step.description || step.goalDescricao,
        goalValueInicial: ((step.initialValue ?? step.goalValueInicial) || 0) / 100,
        goalValueFinal: ((step.finalValue ?? step.goalValueFinal) || DEFAULT_GOAL_FINAL_VALUE) / 100,
        goalMostrarPreview: step.showNextPreview ?? step.goalMostrarPreview,
        isCompleted: step.isCompleted || false,
        completedAt: step.completedAt ? new Date(step.completedAt) : undefined,
        actualValueWhenCompleted: step.actualValueWhenCompleted
      }));
    }
  }

  // 🎯 Exibir notificação de evento (dual-icon system)
  private showEventNotification(event: EventMetadata): void {
    console.log('[GOAL] 🎯 Evento recebido:', event);
    
    const mapping = this.EVENT_SOURCE_MAP[event.source as keyof typeof this.EVENT_SOURCE_MAP];
    
    if (!mapping) {
      console.warn('[GOAL] ❌ Tipo de evento desconhecido:', event.source);
      return;
    }
    
    console.log('[GOAL] 📍 Mapping encontrado:', mapping);

    // Determinar tipo de evento
    let eventType: keyof typeof this.EVENT_TYPE_CONFIG = mapping.type;
    
    // Se for gift subscription
    if (event.isGift) {
      eventType = 'GIFT_SUB';
    }

    // Buscar configurações
    // Se não tiver plataforma (DONATION/SALE), usa config do SITE
    const platformConfig = mapping.platform 
      ? this.PLATFORM_CONFIG[mapping.platform as keyof typeof this.PLATFORM_CONFIG]
      : this.PLATFORM_CONFIG.SITE;
    const typeConfig = this.EVENT_TYPE_CONFIG[eventType];

    console.log('[GOAL] 🎨 Platform config:', platformConfig);
    console.log('[GOAL] 🎨 Type config:', typeConfig);
    
    // Validações extras para debug
    if (mapping.platform && !platformConfig) {
      console.error('[GOAL] ❌ PLATFORM_CONFIG não encontrado para:', mapping.platform);
      return;
    }

    if (!typeConfig) {
      console.warn('[GOAL] ❌ Configuração de tipo não encontrada:', eventType);
      return;
    }

    // Construir notificação (donations/sales não têm platformIcon, apenas typeIcon)
    // Suporta tanto 'icon' (Material Icons) quanto 'iconPath' (imagens PNG)
    const typeIcon = 'icon' in typeConfig ? typeConfig.icon : undefined;
    const typeIconPath = 'iconPath' in typeConfig ? typeConfig.iconPath : undefined;

    this.notificationData = {
      platformIconPath: platformConfig?.iconPath,
      platformColor: platformConfig?.color || '#666',
      typeIcon: typeIcon,
      typeIconPath: typeIconPath,
      contributorName: event.contributorName,
      displayValue: this.formatAmountAdded(event.amountInCents || 0),
    };

    console.log('[GOAL] 📦 Notification data:', this.notificationData);

    this.showNotification = true;

    setTimeout(() => {
      this.showNotification = false;
      this.cdr.detectChanges();
    }, 5000);
  }

  // 🎯 NOVO: Formatar valor em reais
  private formatAmountAdded(amountInCents: number): string {
    const reais = amountInCents / 100;
    return `+R$ ${reais.toFixed(2).replace('.', ',')}`;
  }

}